import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useImmer } from 'use-immer';

import { trackTdLcvCompareEvents } from '@/components/hooks/td';
import useCheckInitialRender from '@/components/hooks/useCheckInitialRender';
import { useMediaQuery } from '@/components/hooks/useMediaQuery';
import CustomMetatag from '@/components/layouts/custom-metatag';
import Layout from '@/components/layouts/lcv';
import { CompareOptionGroup } from '@/components/sections/product-compare/compare-selects/compare-select';
import ProductCompareLCV from '@/components/sections/product-compare/product-compare-lcv';
import { HeadingLevel1 } from '@/components/shared/headline/heading-level-1';
import { Section } from '@/components/shared/layout/section';
import { filterUniqueItems } from '@/components/shared/utility/add-ons';
import { isServer } from '@/components/shared/utility/browser';
import { normaliseToArray } from '@/components/shared/utility/format';
import { normalisePathname } from '@/components/shared/utility/router';
import { HEADLINE, METATAG } from '@/contents/lcv/lcv-compare';
import { LcvModel } from '@/services/contentstack/models/lcv-model';
import { LcvModelColor } from '@/services/contentstack/models/lcv-model-color';
import { getLineUpModelsAndDefaultCompareModels } from '@/services/contentstack/services/lcv-config-service';
import { config } from '@/stitches.config';
import { trackGtmLcvCompareEvents } from '@/tag-manager';

const MAX_MODEL = 3;

const FooterLCVGroup = dynamic(
  () => import('@/components/layouts/lcv/footer-group'),
  {
    ssr: false,
  }
);
export async function getStaticProps() {
  const [lineupModels, defaultCompareModels] =
    await getLineUpModelsAndDefaultCompareModels();

  const selectableModelGroups = lineupModels.map((o) => ({
    ...o.lcv_model_category[0],
    models: o.lcv_model.map((model) => ({
      name: model.name,
      key: model.key,
    })),
  }));

  const selectableModelKeysToModelsMap = new Map<string, LcvModel>();
  lineupModels.forEach((group) =>
    group.lcv_model.forEach((model) => {
      selectableModelKeysToModelsMap[model.key] = model;
    })
  );

  const defaultSelectedModelKeys = defaultCompareModels.map((o) => o.key);

  return {
    props: JSON.parse(
      JSON.stringify({
        selectableModelGroups,
        selectableModelKeysToModelsMap,
        defaultSelectedModelKeys,
      } as unknown as PageProps)
    ),
  };
}

interface PageProps {
  selectableModelGroups: CompareOptionGroup[];
  selectableModelKeysToModelsMap: Map<string, LcvModel>;
  defaultSelectedModelKeys: string[];
}

const Page = (props: PageProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const isInitialRender = useCheckInitialRender();

  const isMobile = useMediaQuery(config.media.maxlg);
  const [
    activateSwiperKeyboardEventIndex,
    setActivateSwiperKeyboardEventIndex,
  ] = useState<number>(0);

  const [selectedModelKeys, setSelectedModelKeys] = useImmer<string[]>(
    props.defaultSelectedModelKeys
  );

  const [selectedModelColors, setSelectedModelColors] = useImmer<
    LcvModelColor[]
  >(
    props.defaultSelectedModelKeys.map(
      (modelKey) =>
        props.selectableModelKeysToModelsMap[modelKey]?.default_color?.[0]
    )
  );

  const onModelChange = (modelIndex: number, modelKey: string) => {
    setActivateSwiperKeyboardEventIndex(modelIndex);
    const isResetLastTabMobile =
      modelIndex === 2 && modelKey === '' && isMobile;

    const updateSelectedModelKeys = [...selectedModelKeys];

    updateSelectedModelKeys[modelIndex] = modelKey;

    const updateSelectedModels = updateSelectedModelKeys.map(
      (modelKey) => props.selectableModelKeysToModelsMap[modelKey]
    );

    setSelectedModelKeys(updateSelectedModelKeys);

    const updateSelectedModelColors = [...selectedModelColors];
    updateSelectedModelColors[modelIndex] =
      props.selectableModelKeysToModelsMap[modelKey]?.default_color?.[0];
    setSelectedModelColors(updateSelectedModelColors);

    // trackAnalyticsEvents when model is changed except when clear mobile 3rd option
    if (!isResetLastTabMobile) {
      trackAnalyticsEvents(updateSelectedModels, updateSelectedModelColors);
    }
  };

  const onColorChange = (colorIndex: number, color: LcvModelColor) => {
    const updateSelectedModelColors = [...selectedModelColors];
    updateSelectedModelColors[colorIndex] = color;
    setSelectedModelColors(updateSelectedModelColors);

    const updateSelectedModelKeys = [...selectedModelKeys];
    const updateSelectedModels = updateSelectedModelKeys.map(
      (modelKey) => props.selectableModelKeysToModelsMap[modelKey]
    );

    trackAnalyticsEvents(updateSelectedModels, updateSelectedModelColors);
  };

  const trackAnalyticsEvents = (
    models: LcvModel[],
    modelColors: LcvModelColor[]
  ) => {
    trackGtmLcvCompareEvents({
      models,
      modelColors: modelColors,
    });

    trackTdLcvCompareEvents({
      models,
      modelColors: modelColors,
    });
  };

  const updateUrlQueryParamsPerSelectedModels = (models: LcvModel[]) => {
    if (isServer) return;

    const queryParams = new URLSearchParams(window.location.search);
    queryParams.delete('model');

    const newModels = filterUniqueItems<LcvModel>(models, 'uid');

    for (const model of newModels) {
      if (model?.key) {
        queryParams.append('model', model?.key);
      }
    }

    const normalisedPathname = normalisePathname(pathname);
    router.replace(`${normalisedPathname}?${queryParams.toString()}`, {
      scroll: false,
    });
  };

  const filterModel = (items: any[], keySelector?: string) => {
    const newModels = filterUniqueItems(items, keySelector);
    if (!newModels || newModels.length === 0) return [];
    if (newModels.length >= MAX_MODEL) return newModels.slice(0, MAX_MODEL);
    const models = newModels.concat(
      Array(MAX_MODEL - newModels.length).fill('')
    );
    return models;
  };

  const updateSelectedModelAndColorsPerUrlQueryParams = (
    urlQueryParams: string[]
  ) => {
    const newUrlQueryParams = filterModel(urlQueryParams);

    if (newUrlQueryParams.length === 0) {
      if (isMobile) {
        onModelChange(2, '');
      }
      return;
    }

    const updatedSelectedModelKeys = filterModel(selectedModelKeys);

    const updatedSelectedModelColors = updatedSelectedModelKeys.map(
      (modelKey) =>
        props.selectableModelKeysToModelsMap[modelKey]?.default_color?.[0]
    );

    newUrlQueryParams.forEach((urlQueryParam, urlQueryParamIndex) => {
      const model = Object.values(props.selectableModelKeysToModelsMap).find(
        (model) => model.key === urlQueryParam
      );
      if (model) {
        updatedSelectedModelKeys[urlQueryParamIndex] = model.key;
        updatedSelectedModelColors[urlQueryParamIndex] =
          model?.default_color?.[0];
      }
    });

    if (isMobile) {
      updatedSelectedModelKeys[updatedSelectedModelKeys.length - 1] = '';
      updatedSelectedModelColors[updatedSelectedModelColors.length - 1] =
        undefined;
    }

    const filterSelectedModelKeys = filterModel(updatedSelectedModelKeys);
    const filterSelectedModelColors = filterModel(
      updatedSelectedModelColors,
      'uid'
    );

    setSelectedModelKeys(filterSelectedModelKeys);
    setSelectedModelColors(filterSelectedModelColors);

    // trackAnalyticsEvents when query model is in initial load
    const updateSelectedModels = filterSelectedModelKeys.map(
      (modelKey) => props.selectableModelKeysToModelsMap[modelKey]
    );
    trackAnalyticsEvents(updateSelectedModels, filterSelectedModelColors);
  };

  useEffect(() => {
    if (isMobile && !isInitialRender) {
      onModelChange(2, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  useEffect(() => {
    if (isServer) return;
    const initialSearchParams = new URLSearchParams(window.location.search);

    let urlQueryParams = initialSearchParams.getAll('model');
    urlQueryParams = normaliseToArray(urlQueryParams);
    updateSelectedModelAndColorsPerUrlQueryParams(urlQueryParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedModels = useMemo(() => {
    return selectedModelKeys.map(
      (modelKey) => props.selectableModelKeysToModelsMap[modelKey]
    );
  }, [props.selectableModelKeysToModelsMap, selectedModelKeys]);

  useEffect(() => {
    if (isInitialRender) return;

    updateUrlQueryParamsPerSelectedModels(selectedModels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModelKeys]);

  return (
    <>
      <CustomMetatag {...METATAG} />
      <main>
        <Section overflow="visible" test="section_compare">
          <HeadingLevel1 {...HEADLINE} />
          <ProductCompareLCV
            optionGroups={props?.selectableModelGroups}
            selectedModelKeys={selectedModelKeys}
            activeSliderIndex={activateSwiperKeyboardEventIndex}
            models={selectedModels}
            colors={selectedModelColors}
            onModelChange={onModelChange}
            onColorChange={onColorChange}
            onChangeActiveSlider={setActivateSwiperKeyboardEventIndex}
          />
        </Section>
      </main>
      <FooterLCVGroup />
    </>
  );
};

export default Page;

Page.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};
